'use client';

import { Callout, migrateDoubleDollarMath } from '@examdojo/core/tiptap';
import { NodeAlignment } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-extension/node-alignment-extension';
import { NodeBackground } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-extension/node-background-extension';
import { UiState } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-extension/ui-state-extension';
import { HorizontalRule } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension';
import { Image } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/image-node/image-node-extension';
import { TableHandleExtension } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/extensions/table-handle';
import { TableKit } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/extensions/table-node-extension';
import { TableCellHandleMenu } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/ui/table-cell-handle-menu';
import { TableExtendRowColumnButtons } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/ui/table-extend-row-column-button';
import { TableHandle } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/ui/table-handle/table-handle';
import { TableSelectionOverlay } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/ui/table-selection-overlay';
import { NotionEditorHeader } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-templates/notion-like/notion-like-editor-header';
import { MobileToolbar } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-templates/notion-like/notion-like-editor-mobile-toolbar';
import { NotionToolbarFloating } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-templates/notion-like/notion-like-editor-toolbar-floating';
import { AiMenu } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/ai-menu';
import { useScrollToHash } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/copy-anchor-link-button/use-scroll-to-hash';
import { DragContextMenu } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/drag-context-menu';
import { EmojiDropdownMenu } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/emoji-dropdown-menu';
import { SlashDropdownMenu } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/slash-dropdown-menu';
import type { SlashMenuConfig } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/slash-dropdown-menu/use-slash-dropdown-menu';
import { AiProvider, useAi } from '@examdojo/core/tiptap-react-ui-components/contexts/ai-context';
import { AppProvider } from '@examdojo/core/tiptap-react-ui-components/contexts/app-context';
import { CollabProvider, useCollab } from '@examdojo/core/tiptap-react-ui-components/contexts/collab-context';
import { UserProvider, useUser } from '@examdojo/core/tiptap-react-ui-components/contexts/user-context';
import { useUiEditorState } from '@examdojo/core/tiptap-react-ui-components/hooks/use-ui-editor-state';
import { TIPTAP_AI_APP_ID } from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-collab-utils';
import { Ai } from '@tiptap-pro/extension-ai';
import { getHierarchicalIndexes, TableOfContentData, TableOfContents } from '@tiptap-pro/extension-table-of-contents';
import type { TiptapCollabProvider } from '@tiptap-pro/provider';
import { Editor, Extensions } from '@tiptap/core';
import { Collaboration, isChangeOrigin } from '@tiptap/extension-collaboration';
import { CollaborationCaret } from '@tiptap/extension-collaboration-caret';
import { Emoji, gitHubEmojis } from '@tiptap/extension-emoji';
import { Highlight } from '@tiptap/extension-highlight';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { Mathematics } from '@tiptap/extension-mathematics';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { TextAlign } from '@tiptap/extension-text-align';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import { Typography } from '@tiptap/extension-typography';
import { UniqueID } from '@tiptap/extension-unique-id';
import { Placeholder, Selection } from '@tiptap/extensions';
import { EditorContent, EditorContext, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { Doc as YDoc } from 'yjs';
import Details, { DetailsContent, DetailsSummary } from '@tiptap/extension-details';

export interface BaseEditorProps {
  placeholder?: string;
  editable?: boolean;
  onEditorReady?: (editor: Editor) => void;
  tableOfContent?: {
    onUpdate?: (content: TableOfContentData) => void;
    scrollableParent?: HTMLElement;
  };
  slashMenuConfig?: SlashMenuConfig;
  contentPadding?: string;
}

export interface NotionEditorProps extends BaseEditorProps {
  room?: string;
  editorOptions?: Partial<Editor['options']>;
}

export interface EditorProviderProps extends BaseEditorProps {
  collabProvider?: TiptapCollabProvider;
  ydoc?: YDoc;
  aiToken: string | null;
  customEditorOptions?: Partial<Editor['options']>;
}

/**
 * Loading spinner component shown while connecting to the notion server
 */
export function LoadingSpinner({ text = 'Connecting...' }: { text?: string }) {
  return (
    <div className="spinner-container">
      <div className="spinner-content">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <div className="spinner-loading-text">{text}</div>
      </div>
    </div>
  );
}

/**
 * EditorContent component that renders the actual editor
 */
export function EditorContentArea({ slashMenuConfig }: { slashMenuConfig?: SlashMenuConfig }) {
  const { editor } = useContext(EditorContext)!;
  const { aiGenerationIsLoading, aiGenerationIsSelection, aiGenerationHasMessage, isDragging } =
    useUiEditorState(editor);

  // Selection based effect to handle AI generation acceptance
  useEffect(() => {
    if (!editor) return;

    if (!aiGenerationIsLoading && aiGenerationIsSelection && aiGenerationHasMessage) {
      editor.chain().focus().aiAccept().run();
      editor.commands.resetUiState();
    }
  }, [aiGenerationHasMessage, aiGenerationIsLoading, aiGenerationIsSelection, editor]);

  useScrollToHash();

  if (!editor) {
    return null;
  }

  return (
    <EditorContent
      editor={editor}
      role="presentation"
      className={`notion-like-editor-content ${editor.isEditable ? 'editable' : ''}`}
      style={{
        cursor: isDragging ? 'grabbing' : 'auto',
      }}
    >
      <DragContextMenu />
      <AiMenu />
      <EmojiDropdownMenu />
      {/* <MentionDropdownMenu /> */}
      <SlashDropdownMenu config={slashMenuConfig} />
      <NotionToolbarFloating />

      {createPortal(<MobileToolbar />, document.body)}
    </EditorContent>
  );
}

/**
 * Component that creates and provides the editor instance
 */
export function EditorProvider(props: EditorProviderProps) {
  const {
    collabProvider,
    ydoc,
    placeholder = 'Start writing...',
    aiToken,
    customEditorOptions = {},
    editable = true,
    onEditorReady,
    tableOfContent,
    slashMenuConfig,
    contentPadding,
  } = props;

  const { user } = useUser();
  const editorRef = useRef<Editor | null>(null);

  const collabExtensions =
    collabProvider && ydoc
      ? [
          Collaboration.configure({ document: ydoc }),
          CollaborationCaret.configure({
            provider: collabProvider,
            user: { id: user.id, name: user.name, color: user.color },
          }),
        ]
      : [];

  const aiExtension = aiToken
    ? Ai.configure({
        appId: TIPTAP_AI_APP_ID,
        token: aiToken,
        autocompletion: false,
        showDecorations: true,
        hideDecorationsOnStreamEnd: false,
        onLoading: (context) => {
          context.editor.commands.aiGenerationSetIsLoading(true);
          context.editor.commands.aiGenerationHasMessage(false);
        },
        onChunk: (context) => {
          context.editor.commands.aiGenerationSetIsLoading(true);
          context.editor.commands.aiGenerationHasMessage(true);
        },
        onSuccess: (context) => {
          const hasMessage = !!context.response;
          context.editor.commands.aiGenerationSetIsLoading(false);
          context.editor.commands.aiGenerationHasMessage(hasMessage);
        },
      })
    : undefined;

  const defaultNotionExtensions = [
    StarterKit.configure({
      ...(collabProvider ? { undoRedo: false } : {}),
      horizontalRule: false,
      dropcursor: {
        width: 2,
      },
      link: { openOnClick: false },
    }),
    HorizontalRule,
    Details.configure({
      persist: true,
      HTMLAttributes: {
        class: 'details',
      },
    }),
    DetailsSummary,
    DetailsContent,
    Callout,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ...collabExtensions,
    ...(aiExtension ? [aiExtension] : []),
    Placeholder.configure({
      placeholder,
      emptyNodeClass: 'is-empty with-slash',
    }),
    // Mention,
    Emoji.configure({
      emojis: gitHubEmojis.filter((emoji) => !emoji.name.includes('regional')),
      forceFallbackImages: true,
    }),
    TableKit.configure({
      table: {
        resizable: true,
        cellMinWidth: 120,
      },
    }),
    NodeBackground,
    NodeAlignment,
    TextStyle,
    Mathematics.configure({
      inlineOptions: {
        onClick: (node, pos) => {
          if (!editorRef.current?.isEditable) {
            return;
          }
          const katex = prompt('Edit inline math:', node.attrs['latex']);
          if (katex) {
            editorRef.current?.chain().setNodeSelection(pos).updateInlineMath({ latex: katex }).focus().run();
          }
        },
      },

      blockOptions: {
        onClick: (node, pos) => {
          if (!editorRef.current?.isEditable) {
            return;
          }
          const katex = prompt('Edit block math:', node.attrs['latex']);
          if (katex) {
            editorRef.current?.chain().setNodeSelection(pos).updateBlockMath({ latex: katex }).focus().run();
          }
        },
      },

      katexOptions: {
        throwOnError: false,
        displayMode: false,
      },
    }),
    Superscript,
    Subscript,
    Color,
    TaskList,
    TaskItem.configure({ nested: true }),
    Highlight.configure({ multicolor: true }),
    Selection,
    Image,
    TableHandleExtension,
    // ImageUploadNode.configure({
    //   accept: 'image/*',
    //   maxSize: MAX_FILE_SIZE,
    //   limit: 3,
    //   upload: handleImageUpload,
    //   onError: (error) => console.error('Upload failed:', error),
    // }),
    UniqueID.configure({
      types: ['table', 'paragraph', 'bulletList', 'orderedList', 'taskList', 'heading', 'blockquote', 'codeBlock'],
      filterTransaction: (transaction) => !isChangeOrigin(transaction),
    }),
    Typography,
    UiState,
    TableOfContents.configure({
      getIndex: getHierarchicalIndexes,
      onUpdate: (content) => {
        tableOfContent?.onUpdate?.(content);
      },
      scrollParent: (): HTMLElement | Window =>
        tableOfContent?.scrollableParent ?? editorRef.current?.view.dom ?? window,
    }),
  ];

  const editor = useEditor({
    immediatelyRender: false,
    editable,
    ...customEditorOptions,
    extensions: (customEditorOptions.extensions || []).reduce<Extensions>(
      (acc, customExt) => {
        const existingIndex = acc.findIndex((ext) => ext.name === customExt.name);
        if (existingIndex !== -1) {
          acc[existingIndex] = customExt;
        } else {
          acc.push(customExt);
        }
        return acc;
      },
      [...defaultNotionExtensions],
    ),
    editorProps: {
      attributes: {
        class: 'notion-like-editor',
        ...(customEditorOptions.editorProps?.attributes ?? {}),
      },
      ...(customEditorOptions.editorProps ?? {}),
    },
    onCreate: ({ editor: currentEditor }) => {
      // Run custom onCreate callback if provided
      customEditorOptions.onCreate?.({ editor: currentEditor });

      // Migrate double-dollar math strings after collab sync
      if (collabProvider) {
        collabProvider.on('synced', () => {
          migrateDoubleDollarMath(currentEditor);
        });
      } else {
        // If no collaboration, migrate immediately
        migrateDoubleDollarMath(currentEditor);
      }
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  if (!editor) {
    return;
  }

  const wrapperStyle: React.CSSProperties | undefined = contentPadding
    ? ({ '--tt-content-padding': contentPadding } as React.CSSProperties)
    : undefined;

  return (
    <div className="notion-like-editor-wrapper" style={wrapperStyle}>
      <EditorContext.Provider value={{ editor }}>
        {editable && <NotionEditorHeader />}
        <EditorContentArea slashMenuConfig={slashMenuConfig} />

        <TableExtendRowColumnButtons />
        <TableHandle />
        <TableSelectionOverlay
          showResizeHandles={true}
          cellMenu={(props) => (
            <TableCellHandleMenu editor={props.editor} onMouseDown={(e) => props.onResizeStart?.('br')(e)} />
          )}
        />
      </EditorContext.Provider>
    </div>
  );
}

/**
 * Full editor with all necessary providers, ready to use with just a room ID
 */
export function NotionEditor(props: NotionEditorProps) {
  const { room, ...editorProps } = props;

  if (!room) {
    return (
      <AiProvider>
        <NonCollaborativeEditor {...editorProps} />
      </AiProvider>
    );
  }

  return (
    <UserProvider>
      <AppProvider>
        <CollabProvider room={room}>
          <AiProvider>
            <CollaborativeNotionEditorContent {...editorProps} />
          </AiProvider>
        </CollabProvider>
      </AppProvider>
    </UserProvider>
  );
}

function NonCollaborativeEditor(props: Omit<NotionEditorProps, 'room'>) {
  const { aiToken } = useAi();

  if (aiToken === undefined) {
    return;
    //return <LoadingSpinner />; FIXME: The loading component sometimes creats a layout shift
  }

  return <EditorProvider {...props} aiToken={aiToken} customEditorOptions={props.editorOptions} />;
}

/**
 * Internal component that handles the editor loading state with collaboration
 */
export function CollaborativeNotionEditorContent(props: Omit<NotionEditorProps, 'room'>) {
  const { provider, ydoc } = useCollab();
  const { aiToken } = useAi();

  if (!provider || aiToken === undefined) {
    return;
    //return <LoadingSpinner />; FIXME: The loading component sometimes creats a layout shift
  }

  return (
    <EditorProvider
      {...props}
      collabProvider={provider}
      ydoc={ydoc}
      aiToken={aiToken}
      customEditorOptions={props.editorOptions}
    />
  );
}
